import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgePercent,
  Building2,
  CalendarDays,
  Car,
  ChevronDown,
  CreditCard,
  Download,
  FerrisWheel,
  Gift,
  Landmark,
  LockKeyhole,
  Smartphone,
  Telescope,
  TicketPercent,
  X,
  Wallet,
} from "lucide-react";
import { formatPackagePrice } from "../Packages/packages";
import {
  BOOKING_EXCLUSIONS,
  BOOKING_INCLUSIONS,
  getBookingPlaceSlug,
} from "../Booking holiday/Booking.js";
import "./Confirm.css";

function addDays(dateString, days) {
  const date = dateString ? new Date(dateString) : new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
}

function formatDisplayDate(dateString) {
  if (!dateString) return "Not selected";

  const date = new Date(`${dateString}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return "Invalid date";
  }

  return date
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(/ /g, "");
}

function formatEmiMoney(amount) {
  const value = Number(amount || 0);
  const hasPaise = Math.round(value * 100) % 100 !== 0;
  return `Rs ${value.toLocaleString("en-IN", {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

function buildTravellers(adults = 0, children = 0, infants = 0) {
  const travellers = [];

  for (let index = 0; index < Number(adults || 0); index += 1) {
    travellers.push({ type: "Adult" });
  }

  for (let index = 0; index < Number(children || 0); index += 1) {
    travellers.push({ type: "Child" });
  }

  for (let index = 0; index < Number(infants || 0); index += 1) {
    travellers.push({ type: "Infant" });
  }

  return travellers;
}

const paymentModes = [
  { id: "UPI", description: "Make Online Payments Directly from Bank", Icon: Smartphone },
  { id: "Credit/Debit/ATM Cards", description: "Use VISA, Mastercard, American Express etc.", Icon: CreditCard },
  { id: "Wallets", description: "Choose Mobikwik, Payzapp, PhonePe or Amazon", Icon: Wallet },
  { id: "Net Banking", description: "All Major banks are supported", Icon: Landmark },
  { id: "EMI", description: "HSBC, RBI, ICICI, Yes and others bank for EMI", Icon: CreditCard },
  { id: "Pay with Rewards", description: "Check Your Rewards", Icon: BadgePercent },
  { id: "Gift Card", description: "Redeem Gift Card", Icon: Gift },
  { id: "Google Pay", description: "Pay Easily with Google Pay", Icon: Smartphone, isGooglePay: true },
];

const walletOptions = [
  { icon: "M", name: "MobiKwik" },
  { icon: "P", name: "PayZapp" },
  { icon: "P", name: "PhonePe" },
  { icon: "a", name: "Amazon" },
];

const popularBanks = [
  { icon: "i", name: "ICICI Bank" },
  { icon: "p", name: "Punjab National Bank" },
  { icon: "s", name: "State Bank of India" },
  { icon: "y", name: "Yes Bank" },
  { icon: "h", name: "HDFC Bank" },
  { icon: "b", name: "Punjab And Sind Bank" },
];

const otherBanks = ["Axis Bank", "Kotak Mahindra Bank", "Bank of Baroda", "Canara Bank"];

const rewardOptions = [
  { icon: "S", name: "Supercoins" },
  { icon: "Z", name: "Zillion" },
  { icon: "I", name: "Indusind Bank" },
  { icon: "Y", name: "Yes Bank" },
  { icon: "M", name: "Intermiles" },
];

const emiCategories = [
  {
    type: "Credit Card",
    banks: [
      { name: "HDFC", tenures: [3, 6, 9, 12, 18, 24], rate: 0.16 },
      { name: "DBS", tenures: [3, 6, 9, 12], rate: 0.15 },
      { name: "ICICI", tenures: [3, 6, 9, 12, 18], rate: 0.16 },
      { name: "IDFC FIRST Bank", tenures: [9, 12], rate: 0.16 },
      { name: "Axis", tenures: [3, 6, 9, 12], rate: 0.15 },
      { name: "Jammu and Kashmir", tenures: [6, 9, 12], rate: 0.14 },
      { name: "HSBC", tenures: [6, 9, 12, 18], rate: 0.17 },
    ],
  },
  {
    type: "No Cost EMI",
    banks: [
      { name: "IDFC FIRST Bank", tenures: [3, 6, 9], rate: 0, discountInterest: true },
      { name: "HDFC", tenures: [3, 6, 9, 12], rate: 0, discountInterest: true },
      { name: "ICICI", tenures: [3, 6], rate: 0, discountInterest: true },
      { name: "Axis", tenures: [3, 6, 9], rate: 0, discountInterest: true },
    ],
  },
  {
    type: "Debit Card EMI",
    banks: [
      { name: "HDFC", tenures: [3, 6, 9, 12], rate: 0.14 },
      { name: "ICICI", tenures: [3, 6, 9], rate: 0.15 },
      { name: "Axis", tenures: [6, 9, 12], rate: 0.15 },
      { name: "Kotak Mahindra Bank", tenures: [3, 6, 9, 12], rate: 0.16 },
    ],
  },
];

export default function Confirm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { place = "", packageSlug = "" } = useParams();

  const placeSlug = getBookingPlaceSlug(place);

  const API_BASE =
    (typeof process !== "undefined" && process.env?.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL
      : "http://localhost:5000"
    ).replace(/\/$/, "");

  const packageId = location.state?.packageId || null;

  const [pkg, setPkg] = useState(null);
  const [packageLoading, setPackageLoading] = useState(true);
  const [packageError, setPackageError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadPackage = async () => {
      setPackageLoading(true);
      setPackageError("");
      setPkg(null);

      try {
        if (!packageId) {
          throw new Error("Booking package is missing.");
        }

        const response = await fetch(
          `${API_BASE}/api/packages/${encodeURIComponent(packageId)}`
        );

        if (response.status === 404) {
          throw new Error("This package is no longer available.");
        }

        if (!response.ok) {
          throw new Error("Unable to load the selected package.");
        }

        const data = await response.json();
        const packageData = data?.data || data;

        if (!packageData?.id) {
          throw new Error("Invalid package returned by server.");
        }

        if (!cancelled) {
          setPkg({
            ...packageData,
            image: packageData.image_url || packageData.image || "",
          });
        }
      } catch (error) {
        if (!cancelled) {
          setPackageError(
            error.message || "Unable to load the selected package."
          );
          setPkg(null);
        }
      } finally {
        if (!cancelled) {
          setPackageLoading(false);
        }
      }
    };

    loadPackage();

    return () => {
      cancelled = true;
    };
  }, [API_BASE, packageId]);

/*
 * The Holiday payment policy is backend-authoritative and is returned in the
 * booking-creation response (pricing.payment_policy / payment). There is no
 * public policy endpoint, so the UI shows the policy once the booking exists.
 */
const [holidayPaymentPolicy, setHolidayPaymentPolicy] = useState(null);
const [backendPaymentAmount, setBackendPaymentAmount] = useState(null);

  const [accepted, setAccepted] = useState(true);
  const [donation, setDonation] = useState(0);
  const [showPayment, setShowPayment] = useState(false);
  const [timer, setTimer] = useState(9 * 60 + 9);
const adults = Number(location.state?.adults || 0);
const children = Number(location.state?.children || 0);
const infants = Number(location.state?.infants || 0);

const travelDate = location.state?.travelDate || "";

const isInternational = Number(pkg?.is_international) === 1;

const validateBooking = () => {
  setPackageError("");
  setPaymentError("");

  if (!pkg?.id) {
    setPackageError("Package information is missing.");
    return false;
  }

  if (!travelDate) {
    setPackageError("Please select a travel date.");
    return false;
  }

  if (travellers.length < 1) {
    setPackageError("Please select at least one traveller.");
    return false;
  }

  if (!accepted) {
    setPackageError("Please accept the booking terms and conditions.");
    return false;
  }

  const incompleteTraveller = travellerForms.some(
    (traveller) =>
      !traveller.firstName.trim() ||
      !traveller.lastName.trim() ||
      !traveller.dob
  );

  if (incompleteTraveller) {
    setPackageError(
      "Please enter first name, last name and date of birth for every traveller."
    );
    return false;
  }

  if (isInternational) {
    const incompletePassport = travellerForms.some(
      (traveller) =>
        !traveller.passportNumber.trim() ||
        !traveller.passportExpiry
    );

    if (incompletePassport) {
      setPackageError(
        "Passport number and passport expiry are required for international packages."
      );
      return false;
    }
  }

  if (!contact.email.trim()) {
    setPackageError("Please enter your email address.");
    return false;
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) {
    setPackageError("Please enter a valid email address.");
    return false;
  }

  const phone = contact.phone.replace(/\D/g, "");

  if (!phone || phone.length < 7 || phone.length > 15) {
    setPackageError("Please enter a valid phone number.");
    return false;
  }

  return true;
};

const travellers = buildTravellers(adults, children, infants);

  const initialTravellerForms = useMemo(
    () => travellers.map((traveller) => ({
      ...traveller,
      title: "Mr",
      firstName: "",
      lastName: "",
      dob: "",
      passportNumber: "",
      passportExpiry: "",
      meal: "",
    })),
    [travellers.length]
  );
  const [travellerForms, setTravellerForms] = useState(initialTravellerForms);
  const [specialRequest, setSpecialRequest] = useState("");
  const [contact, setContact] = useState({ email: "", countryCode: "+91", phone: "" });
  const [gstEnabled, setGstEnabled] = useState(false);
  const [couponCode, setCouponCode] = useState("");
const [selectedCoupon, setSelectedCoupon] = useState(false);
const [couponDiscount, setCouponDiscount] = useState(0);
  const [paymentMode, setPaymentMode] = useState("UPI");
  const [upi, setUpi] = useState({ id: "", provider: "" });
  const [selectedWallet, setSelectedWallet] = useState("");
  const [selectedBank, setSelectedBank] = useState("");
  const [otherBank, setOtherBank] = useState("");
  const [selectedReward, setSelectedReward] = useState("Supercoins");
  const [giftCardCode, setGiftCardCode] = useState("");
const [giftCardApplied, setGiftCardApplied] = useState(false);
const [giftCardDiscount, setGiftCardDiscount] = useState(0);
const [bookingId, setBookingId] = useState(null);
const [bookingReference, setBookingReference] = useState("");
const [paymentLoading, setPaymentLoading] = useState(false);
const [paymentError, setPaymentError] = useState("");
  const [emiType, setEmiType] = useState("Credit Card");
  const [emiBank, setEmiBank] = useState("HDFC");
  const [emiTenure, setEmiTenure] = useState(6);
  const [emiCard, setEmiCard] = useState({ number: "", holderName: "", month: "", year: "", cvv: "" });
  const [emiOpen, setEmiOpen] = useState({ bank: true, tenure: false, card: false });
  const [googlePay, setGooglePay] = useState({ id: "", provider: "" });
  const [cardPayment, setCardPayment] = useState({
    number: "",
    validThrough: "",
    cvv: "",
    holderName: "",
    saveCard: false,
  });
    

  const nights =
    Number(
      String(pkg?.duration || "").match(/(\d+)\s*N/i)?.[1]
    ) || 0;

  const endDate =
    travelDate && nights > 0
      ? addDays(travelDate, nights)
      : "";

  const pricingBasis = pkg?.pricing_basis || "per_person";

// Display amounts in the package's saved currency (numeric values are
// unchanged — no FX conversion happens anywhere in the booking flow).
const money = (value) => formatPackagePrice(value, pkg?.currency || "INR");

const basePrice = Number(pkg?.base_price || 0);
const discountPercent = Number(pkg?.discount_percent || 0);
const discountAmount = Number(pkg?.discount_amount || 0);
const sellingPrice = Number(pkg?.selling_price || 0);
const finalPrice = Number(pkg?.final_price || 0);

const gst = Number(pkg?.gst || 0);
const tax = Number(pkg?.other_tax || 0);
const serviceFee = Number(pkg?.service_fee || 0);

const bookingQuantity =
  pricingBasis === "per_booking"
    ? 1
    : pricingBasis === "per_person"
      ? Math.max(1, travellers.length)
      : 1;

const packageSubtotal = Number(
  (finalPrice * bookingQuantity).toFixed(2)
);

const payableBeforeDiscounts = Number(packageSubtotal.toFixed(2));

const payableTotal = Math.max(
  Number(
    (
      payableBeforeDiscounts -
      couponDiscount -
      giftCardDiscount
    ).toFixed(2)
  ),
  0
);

const pricingBasisLabel =
  pricingBasis === "per_room"
    ? "Per Room"
    : pricingBasis === "per_booking"
      ? "Per Booking"
      : "Per Person";
  const activeEmiCategory = emiCategories.find((category) => category.type === emiType) || emiCategories[0];
  const activeEmiBank = activeEmiCategory.banks.find((bank) => bank.name === emiBank) || activeEmiCategory.banks[0];
  const emiInterestRate = activeEmiBank.rate;
  const emiPlans = activeEmiBank.tenures.map((tenure) => {
    const interest = (payableTotal * emiInterestRate * tenure) / 12;
    const totalPayable = activeEmiBank.discountInterest ? payableTotal : payableTotal + interest;
    return {
      tenure,
      principal: payableTotal,
      monthly: totalPayable / tenure,
      interest,
      totalPayable,
    };
  });
  const selectedEmiPlan = emiPlans.find((plan) => plan.tenure === emiTenure) || emiPlans[0];
  const isEmiCardReady = Boolean(
    emiCard.number.trim()
    && emiCard.holderName.trim()
    && emiCard.month
    && emiCard.year
    && emiCard.cvv.trim()
  );

  useEffect(() => {
    if (!showPayment) return undefined;
    const interval = setInterval(() => {
      setTimer((current) => Math.max(0, current - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [showPayment]);

  useEffect(() => {
    setTravellerForms(initialTravellerForms);
  }, [initialTravellerForms]);

  const updateTraveller = (index, key, value) => {
    setTravellerForms((current) => current.map((traveller, travellerIndex) => (
      travellerIndex === index ? { ...traveller, [key]: value } : traveller
    )));
  };

const applyCoupon = async () => {
  const code = couponCode.trim();

  if (!code) {
    setPackageError("Please enter a coupon code.");
    return;
  }

  if (!pkg?.id || packageSubtotal <= 0) {
    setPackageError("Unable to validate coupon for this booking.");
    return;
  }

  try {
    setPackageError("");

    const response = await fetch(
      `${API_BASE}/api/coupons/validate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code,
          package_id: pkg.id,
          booking_amount: packageSubtotal,
          user_id: location.state?.userId || null,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok || !data?.success) {
      setSelectedCoupon(false);
      setCouponDiscount(0);
      setPackageError(
        data?.error || "Unable to apply this coupon."
      );
      return;
    }

    const discount = Math.max(
      0,
      Number(data.discount_amount || 0)
    );

    setCouponDiscount(discount);
    setSelectedCoupon(true);
  } catch (error) {
    console.error("Coupon validation failed:", error);

    setSelectedCoupon(false);
    setCouponDiscount(0);

    setPackageError(
      "Unable to validate the coupon. Please try again."
    );
  }
};

  const selectEmiType = (type) => {
    const category = emiCategories.find((item) => item.type === type) || emiCategories[0];
    setEmiType(type);
    setEmiBank(category.banks[0].name);
    setEmiTenure(category.banks[0].tenures[0]);
    setEmiOpen({ bank: true, tenure: false, card: false });
  };

  const selectEmiBank = (bank) => {
    const bankDetail = activeEmiCategory.banks.find((item) => item.name === bank) || activeEmiCategory.banks[0];
    setEmiBank(bankDetail.name);
    setEmiTenure(bankDetail.tenures[0]);
    setEmiOpen({ bank: true, tenure: true, card: false });
  };

  const selectEmiTenure = (tenure) => {
    setEmiTenure(tenure);
    setEmiOpen({ bank: true, tenure: true, card: true });
  };

  const createHolidayBooking = async () => {
  if (bookingId) {
    return {
      id: bookingId,
      booking_reference: bookingReference,
    };
  }

  setPaymentError("");
  setPaymentLoading(true);

  try {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken");

    if (!token) {
      throw new Error("Please sign in before booking.");
    }

    const response = await fetch(
      `${API_BASE}/api/holiday-bookings`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          package_id: pkg.id,
          travel_date: travelDate,
          travellers: travellerForms,
traveller_summary: {
  adults,
  children,
  infants,
},
          special_request: specialRequest || null,
          contact: {
            email: contact.email.trim(),
            country_code: contact.countryCode,
            phone: contact.phone.trim(),
          },
          gst_enabled: gstEnabled,
          coupon_code: selectedCoupon ? couponCode.trim() : null,
          gift_card_code: giftCardApplied ? giftCardCode.trim() : null,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data?.error ||
          data?.message ||
          "Unable to create holiday booking."
      );
    }

    const booking = data?.data || data?.booking || data;

    if (!booking?.id) {
      throw new Error("Booking was created but no booking ID was returned.");
    }

    setBookingId(booking.id);
    setBookingReference(
      booking.booking_reference ||
        booking.reference ||
        ""
    );

    /* Backend-authoritative payment policy and amount (returned with the booking) */
    if (data?.pricing?.payment_policy) {
      setHolidayPaymentPolicy(data.pricing.payment_policy);
    }

    if (data?.payment) {
      setBackendPaymentAmount({
        amount: Number(data.payment.amount || 0),
        total_amount: Number(data.payment.total_amount || 0),
        amount_due: Number(data.payment.amount_due || 0),
        final_payment_deadline: data.payment.final_payment_deadline || null,
        grace_period_until: data.payment.grace_period_until || null,
      });
    }

    return booking;
  } catch (error) {
    const message =
      error.message || "Unable to create holiday booking.";

    setPaymentError(message);
    throw error;
  } finally {
    setPaymentLoading(false);
  }
};

 const openPaymentPopup = async () => {
  if (!validateBooking()) {
    return;
  }

  await initiateHolidayPayment();
};

const initiateHolidayPayment = async () => {
  setPaymentError("");
  setPaymentLoading(true);

  try {
    const booking = await createHolidayBooking();

    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken");

    if (!token) {
      throw new Error("Please sign in before making payment.");
    }

    const response = await fetch(
      `${API_BASE}/api/payment/easebuzz/holiday/initiate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          booking_id: booking.id,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data?.error ||
          data?.message ||
          "Unable to initiate holiday payment."
      );
    }

    const paymentUrl =
      data?.payment_url ||
      data?.data?.payment_url ||
      data?.data?.paymentLink ||
      data?.paymentLink;

    if (!paymentUrl) {
      throw new Error(
        "Payment was initiated but Easebuzz payment URL was not returned."
      );
    }

    window.location.href = paymentUrl;
  } catch (error) {
    setPaymentError(
      error.message || "Unable to initiate payment."
    );
  } finally {
    setPaymentLoading(false);
  }
};

  const timerText = `${String(Math.floor(timer / 60)).padStart(2, "0")}:${String(timer % 60).padStart(2, "0")}`;

    if (packageLoading) {
    return (
      <main className="holiday-confirm-page">
        <section className="holiday-confirm-empty">
          <h1>Loading booking details...</h1>
          <p>Please wait while we load the latest package information.</p>
        </section>
      </main>
    );
  }

  if (packageError || !pkg) {
    return (
      <main className="holiday-confirm-page">
        <section className="holiday-confirm-empty">
          <h1>Package not available</h1>
          <p>
            {packageError || "The selected package could not be found."}
          </p>

          <button
            type="button"
            onClick={() =>
              navigate(
                placeSlug
                  ? `/holidays/packages/${placeSlug}`
                  : "/holidays"
              )
            }
          >
            <ArrowLeft size={16} />
            Back to Holidays
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="holiday-confirm-page">
      <section className="holiday-confirm-shell">
        <div className="holiday-confirm-layout">
          <div className="holiday-confirm-main">
            <section className="confirm-package-card">
              <div className="confirm-package-top">
                {pkg.image ? (
  <img src={pkg.image} alt={pkg.title} />
) : (
  <div className="confirm-package-image-placeholder">
    Package image unavailable
  </div>
)}
                <div className="confirm-package-copy">
                  <p>Package Detail</p>
                  <h1>{pkg.title}</h1>
                  <span>Duration : <b>{pkg.duration.replace("/", " / ")}</b></span>
                  <span>No of Travellers : <b>{travellers.length}</b></span>

                  <div className="confirm-date-box">
                    <div>
                      <CalendarDays size={18} />
                      <span>Start Date</span>
                      <strong>{formatDisplayDate(travelDate)}</strong>
                    </div>
                    <div className="confirm-nights">
                      <b>{nights} N</b>
                    </div>
                    <div>
                      <span>End Date</span>
                      <strong>{formatDisplayDate(endDate)}</strong>
                      <CalendarDays size={18} />
                    </div>
                  </div>

                  <div className="confirm-icons">
                    <Building2 />
                    <Telescope />
                    <Car />
                    <FerrisWheel />
                  </div>
                </div>
              </div>
              <details>
                <summary>Inclusions <b>[ + ]</b></summary>
                <ul>{BOOKING_INCLUSIONS.slice(0, 5).map((item) => <li key={item}>{item}</li>)}</ul>
              </details>
              <details>
                <summary>Exclusions <b>[ + ]</b></summary>
                <ul>{BOOKING_EXCLUSIONS.map((item) => <li key={item}>{item}</li>)}</ul>
              </details>
            </section>

            <section className="confirm-donation-card">
              <div>
                <h2>Help us preserve India's Heritage & Green Spaces!</h2>
                <p>Support the EaseMyTrip Foundation to maintain greenery & cleanliness across parks and ASI monuments for future generations. <button type="button">Know More</button></p>
                <div className="confirm-donation-options">
                  {[10, 20, 50, 100].map((amount) => (
                    <button
                      type="button"
                      key={amount}
                      className={donation === amount ? "is-active" : ""}
                      onClick={() => setDonation(amount)}
                    >
                      ₹ {amount}
                    </button>
                  ))}
                </div>
              </div>
              <span className="confirm-plant">🌱</span>
            </section>

            <section className="confirm-form-card">
              <h2>Travellers Details</h2>
              {travellerForms.map((traveller, index) => (
                <article className="confirm-traveller" key={`traveller-${index + 1}`}>
                  <h3>Traveller {index + 1}</h3>
                  <p>{traveller.type}</p>
                  <div className="confirm-title-buttons">
                    {["Mr", "Mrs", "Miss", "Master"].map((title, titleIndex) => (
                      <button type="button" className={traveller.title === title ? "is-active" : ""} key={title} onClick={() => updateTraveller(index, "title", title)}>{title}</button>
                    ))}
                  </div>
                  <div className="confirm-field-grid">
                    <label>First Name<input value={traveller.firstName} onChange={(event) => updateTraveller(index, "firstName", event.target.value)} placeholder="Enter First Name" /></label>
                    <label>Last Name<input value={traveller.lastName} onChange={(event) => updateTraveller(index, "lastName", event.target.value)} placeholder="Enter Last Name" /></label>
                    <label>Date of birth<input type="date" value={traveller.dob} onChange={(event) => updateTraveller(index, "dob", event.target.value)} /></label>
                    {isInternational && (
  <>
    <label>
      Passport Number
      <input
        value={traveller.passportNumber}
        onChange={(event) =>
          updateTraveller(index, "passportNumber", event.target.value)
        }
        placeholder="ENTER PASSPORT NUMBER"
      />
    </label>

    <label>
      Passport Expiry
      <input
        type="date"
        value={traveller.passportExpiry}
        onChange={(event) =>
          updateTraveller(index, "passportExpiry", event.target.value)
        }
      />
    </label>
  </>
)}
<label>Meal<select value={traveller.meal} onChange={(event) => updateTraveller(index, "meal", event.target.value)}><option value="">-- Select a Meal --</option><option>Veg Meal</option><option>Non Veg Meal</option><option>Jain Meal</option></select></label>
                  </div>
                </article>
              ))}
            </section>

            <section className="confirm-simple-card">
              <h2>Special Request (optional)</h2>
              <input value={specialRequest} onChange={(event) => setSpecialRequest(event.target.value)} placeholder="Enter your special request here" />
            </section>

            <section className="confirm-simple-card">
              <h2>Contact Information</h2>
              <div className="confirm-contact-grid">
                <label>Email Address<input value={contact.email} onChange={(event) => setContact((current) => ({ ...current, email: event.target.value }))} placeholder="Enter Email Address" /></label>
                <label>Phone Number<div className="confirm-phone"><select value={contact.countryCode} onChange={(event) => setContact((current) => ({ ...current, countryCode: event.target.value }))}><option>+91</option><option>+1</option><option>+44</option><option>+971</option></select><input value={contact.phone} onChange={(event) => setContact((current) => ({ ...current, phone: event.target.value }))} placeholder="Enter Phone Number" /></div></label>
              </div>
            </section>

            <label className="confirm-gst">
              <input type="checkbox" checked={gstEnabled} onChange={(event) => setGstEnabled(event.target.checked)} />
              <span><b>Use GST for this booking</b> (Optional)<small>To claim credit of GST charged by Cab Vendor/EaseMyTrip, please enter your company's GST number</small></span>
            </label>

            <label className="confirm-agree">
              <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
              <span>I understand and agree to the rules, <b>Privacy Policy</b>, <b>User Agreement</b> and <b>Terms & Conditions</b> of EaseMyTrip.</span>
            </label>

            <button className="confirm-bottom-continue" type="button" disabled={!accepted} onClick={openPaymentPopup}>Continue Booking <ArrowRight size={16} /></button>
          </div>

          <aside className="holiday-confirm-side">
            <button className="confirm-download" type="button"><Download size={16} /> Download PDF</button>
            <section className="confirm-price-box">
              <h2>Price Details</h2>
              <p>
  <span>
    Package Price × {bookingQuantity}{" "}
    <small>{pricingBasisLabel}</small>
  </span>
  <b>{money(packageSubtotal)}</b>
</p>

{gst > 0 && (
  <p>
    <span>GST</span>
    <b>{money(gst)}</b>
  </p>
)}

{tax > 0 && (
  <p>
    <span>Other Tax</span>
    <b>{money(tax)}</b>
  </p>
)}

{serviceFee > 0 && (
  <p>
    <span>Service Fee</span>
    <b>{money(serviceFee)}</b>
  </p>
)}

{donation > 0 && (
  <p>
    <span>Green Donation</span>
    <b>{money(donation)}</b>
  </p>
)}

{couponDiscount > 0 && (
  <p className="discount">
    <span>Coupon Discount</span>
    <b>-{money(couponDiscount)}</b>
  </p>
)}

{giftCardDiscount > 0 && (
  <p className="discount">
    <span>Gift Card</span>
    <b>-{money(giftCardDiscount)}</b>
  </p>
)}

<p className="grand">
  <span>Grand Total</span>
  <b>{money(payableTotal)}</b>
</p>
            </section>
              <section className="confirm-coupon-box">
  <h2>Have a coupon code?</h2>

  <div className="confirm-coupon-input">
    <input
      value={couponCode}
      onChange={(event) => setCouponCode(event.target.value)}
      placeholder="Enter Coupon Code"
    />

    <button type="button" onClick={applyCoupon}>
      <TicketPercent size={14} />
      Apply
    </button>
  </div>

  {selectedCoupon && couponCode && (
    <p className="discount">
      Coupon: <b>{couponCode}</b>
    </p>
  )}
</section>
            <section className="confirm-pay-box">
  <h2>Payment</h2>

  {holidayPaymentPolicy ? (
    holidayPaymentPolicy.payment_mode === "partial" ? (
      <>
        <p>
          <span>
            Initial Payment ({
              Number(holidayPaymentPolicy.initial_payment_percent || 0)
            }%)
          </span>

          <b>
            {money(
              backendPaymentAmount
                ? backendPaymentAmount.amount
                : Number(
                    (
                      payableTotal *
                      Number(
                        holidayPaymentPolicy.initial_payment_percent || 0
                      )
                    ) / 100
                  ).toFixed(2)
            )}
          </b>
        </p>

        {backendPaymentAmount?.final_payment_deadline && (
          <small>
            Final payment due before:{" "}
            {formatDisplayDate(
              String(backendPaymentAmount.final_payment_deadline).slice(0, 10)
            )}
          </small>
        )}

        <small>
          Remaining amount will be payable according to the installment
          schedule.
        </small>
      </>
    ) : (
      <p>
        <span>Make Full Payment</span>
        <b>
          {money(
            backendPaymentAmount
              ? backendPaymentAmount.amount
              : payableTotal
          )}
        </b>
      </p>
    )
  ) : (
    <p>
      <span>Payment amount will be confirmed after booking creation</span>
    </p>
  )}
</section>
             </aside>
        </div>
      </section>

      <div className="confirm-mobile-bookbar">
        <div>
          <span>Grand Total</span>
          <strong>{money(payableTotal)}</strong>
        </div>
        <button type="button" disabled={!accepted} onClick={openPaymentPopup}>Book Now <ArrowRight size={16} /></button>
      </div>

      {showPayment && (
        <div className="confirm-payment-overlay" onClick={(event) => event.target === event.currentTarget && setShowPayment(false)}>
          <section className="confirm-payment-modal" role="dialog" aria-modal="true" aria-label="Payment mode">
            <button className="confirm-payment-close" type="button" onClick={() => setShowPayment(false)}><X size={18} /></button>
            <div className="confirm-payment-head">
              <h2>Payment Mode</h2>
              <span>◷ The session will expire in: <b>{timerText}</b></span>
            </div>
            <div className="confirm-payment-box">
              {paymentError && (
  <div className="confirm-payment-error" role="alert">
    {paymentError}
  </div>
)}
              <label className="mobile-payment-mode-select">
                Payment Option
                <select value={paymentMode} onChange={(event) => setPaymentMode(event.target.value)}>
                  {paymentModes.map(({ id }) => <option value={id} key={id}>{id}</option>)}
                </select>
              </label>
              <aside className="confirm-payment-tabs">
                {paymentModes.map(({ id, description, Icon, isGooglePay }) => (
                  <button
                    className={paymentMode === id ? "is-active" : ""}
                    type="button"
                    key={id}
                    onClick={() => setPaymentMode(id)}
                  >
                    <span className={`payment-method-icon ${isGooglePay ? "google-pay-tab-icon" : ""}`}>
                      {isGooglePay ? <b>G</b> : <Icon size={22} />}
                    </span>
                    <strong>{id}</strong>
                    <small>{description}</small>
                  </button>
                ))}
              </aside>

              {paymentMode === "Credit/Debit/ATM Cards" ? (
                <div className="confirm-card-panel">
                  <label>Enter Your Card No.</label>
                  <input
                    value={cardPayment.number}
                    onChange={(event) => setCardPayment((current) => ({ ...current, number: event.target.value }))}
                    placeholder="ENTER CARD NUMBER"
                  />
                  <div className="confirm-card-row">
                    <label>
                      Valid Through (MM/YY)
                      <span>
                        <input
                          value={cardPayment.validThrough}
                          onChange={(event) => setCardPayment((current) => ({ ...current, validThrough: event.target.value }))}
                          placeholder="MM/YY"
                        />
                        <b>ⓘ</b>
                      </span>
                    </label>
                    <label>
                      CVV
                      <span>
                        <input
                          value={cardPayment.cvv}
                          onChange={(event) => setCardPayment((current) => ({ ...current, cvv: event.target.value }))}
                          placeholder="CVV"
                        />
                        <b>ⓘ</b>
                      </span>
                    </label>
                  </div>
                  <label>Enter Card Holder Name.</label>
                  <input
                    value={cardPayment.holderName}
                    onChange={(event) => setCardPayment((current) => ({ ...current, holderName: event.target.value }))}
                    placeholder="Name On The Card"
                  />
                  <label className="confirm-save-card">
                    <input
                      type="checkbox"
                      checked={cardPayment.saveCard}
                      onChange={(event) => setCardPayment((current) => ({ ...current, saveCard: event.target.checked }))}
                    />
                    Save this card for faster checkout
                  </label>
                  <div className="confirm-card-pay-row">
                    <div className="confirm-total-fare">
                      <span>Total Fare:</span>
                      <strong>{money(payableTotal)}</strong>
                    </div>
                    <button type="button" disabled={paymentLoading} onClick={initiateHolidayPayment}><LockKeyhole size={16} /> {paymentLoading ? "Processing..." : "Make Payment"}</button>
                  </div>
                  <p className="confirm-secure-note">🔒 We use 128-bit secure encryption providing you a SAFE payment environment</p>
                  <p>
                    By Continuing, you agree to the Rules, <b>Privacy Policy</b>, <b>User Agreement</b> and <b>Terms & Conditions</b> of EaseMyTrip
                  </p>
                </div>
              ) : paymentMode === "Wallets" ? (
                <div className="confirm-wallet-panel">
                  <h3>Select Your Wallet</h3>
                  <div className="confirm-wallet-grid">
                    {walletOptions.map(({ icon, name }) => (
                      <label className={selectedWallet === name ? "is-active" : ""} key={name}>
                        <input
                          type="radio"
                          name="wallet"
                          checked={selectedWallet === name}
                          onChange={() => setSelectedWallet(name)}
                        />
                        <span>{icon}</span>
                        {name}
                      </label>
                    ))}
                  </div>
                  <div className="confirm-card-pay-row">
                    <div className="confirm-total-fare">
                      <span>Total Fare:</span>
                      <strong>{money(payableTotal)}</strong>
                    </div>
                    <button type="button" disabled={paymentLoading} onClick={initiateHolidayPayment}><LockKeyhole size={16} /> {paymentLoading ? "Processing..." : "Make Payment"}</button>
                  </div>
                  <p className="confirm-secure-note">🔒 We use 128-bit secure encryption providing you a SAFE payment environment</p>
                  <p>
                    By Continuing, you agree to the Rules, <b>Privacy Policy</b>, <b>User Agreement</b> and <b>Terms & Conditions</b> of EaseMyTrip
                  </p>
                </div>
              ) : paymentMode === "Net Banking" ? (
                <div className="confirm-bank-panel">
                  <h3>Select Popular Banks</h3>
                  <div className="confirm-bank-grid">
                    {popularBanks.map(({ icon, name }) => (
                      <label className={selectedBank === name ? "is-active" : ""} key={name}>
                        <input
                          type="radio"
                          name="bank"
                          checked={selectedBank === name}
                          onChange={() => setSelectedBank(name)}
                        />
                        <span>{icon}</span>
                        {name}
                      </label>
                    ))}
                  </div>
                  <label className="confirm-other-bank">
                    Select Other Banks
                    <select value={otherBank} onChange={(event) => setOtherBank(event.target.value)}>
                      <option value="">Select</option>
                      {otherBanks.map((bank) => <option key={bank}>{bank}</option>)}
                    </select>
                  </label>
                  <div className="confirm-card-pay-row">
                    <div className="confirm-total-fare">
                      <span>Total Fare:</span>
                      <strong>{money(payableTotal)}</strong>
                    </div>
                    <button type="button" disabled={paymentLoading} onClick={initiateHolidayPayment}><LockKeyhole size={16} /> {paymentLoading ? "Processing..." : "Make Payment"}</button>
                  </div>
                  <p className="confirm-secure-note">🔒 We use 128-bit secure encryption providing you a SAFE payment environment</p>
                  <p>
                    By Continuing, you agree to the Rules, <b>Privacy Policy</b>, <b>User Agreement</b> and <b>Terms & Conditions</b> of EaseMyTrip
                  </p>
                </div>
              ) : paymentMode === "EMI" ? (
                <div className="confirm-emi-panel">
                  <h3>Select Banks</h3>
                  <div className="confirm-emi-type-tabs">
                    {emiCategories.map(({ type }) => (
                      <button
                        type="button"
                        key={type}
                        className={emiType === type ? "is-active" : ""}
                        onClick={() => selectEmiType(type)}
                      >
                        {type}
                      </button>
                    ))}
                  </div>

                  <section className="confirm-emi-section">
                    <button
                      className="confirm-emi-section-title"
                      type="button"
                      aria-expanded={emiOpen.bank}
                      onClick={() => setEmiOpen((current) => ({ ...current, bank: !current.bank }))}
                    >
                      <b className={emiBank ? "is-done" : ""}>1</b>
                      <strong>Choose your Bank</strong>
                      <span>You must have its debit/credit card</span>
                      <small><ChevronDown size={18} /></small>
                    </button>
                    {emiOpen.bank && (
                      <div className="confirm-emi-bank-list">
                        {activeEmiCategory.banks.map((bank) => (
                          <label className={emiBank === bank.name ? "is-active" : ""} key={bank.name}>
                            <input
                              type="radio"
                              name="emi-bank"
                              checked={emiBank === bank.name}
                              onChange={() => selectEmiBank(bank.name)}
                            />
                            <span>{bank.name.charAt(0)}</span>
                            {bank.name}
                          </label>
                        ))}
                      </div>
                    )}
                  </section>

                  <section className="confirm-emi-section">
                    <button
                      className="confirm-emi-section-title"
                      type="button"
                      aria-expanded={emiOpen.tenure}
                      onClick={() => setEmiOpen((current) => ({ ...current, tenure: !current.tenure }))}
                    >
                      <b className={emiOpen.tenure ? "is-done" : ""}>2</b>
                      <strong>Choose EMI Tenure</strong>
                      <span>Check interest rates and monthly installment beforehand</span>
                      <small><ChevronDown size={18} /></small>
                    </button>
                    {emiOpen.tenure && (
                      <table className="confirm-emi-table">
                        <thead>
                          <tr>
                            <th></th>
                            <th>Tenure</th>
                            <th>Principal Amount</th>
                            <th>Effective Interest*</th>
                            <th>Monthly Installments</th>
                            <th>Effective Interest paid to bank</th>
                          </tr>
                        </thead>
                        <tbody>
                          {emiPlans.map((plan) => (
                            <tr key={plan.tenure}>
                              <td><input type="radio" name="emi-tenure" checked={emiTenure === plan.tenure} onChange={() => selectEmiTenure(plan.tenure)} /></td>
                              <td>{plan.tenure}</td>
                              <td>{formatEmiMoney(plan.principal)}</td>
                              <td>{Math.round(emiInterestRate * 100)}%(p.a.)</td>
                              <td>{formatEmiMoney(plan.monthly)}</td>
                              <td>{formatEmiMoney(plan.interest)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </section>

                  <section className="confirm-emi-section">
                    <button
                      className="confirm-emi-section-title"
                      type="button"
                      aria-expanded={emiOpen.card}
                      onClick={() => setEmiOpen((current) => ({ ...current, card: !current.card }))}
                    >
                      <b className={emiOpen.card ? "is-done" : ""}>3</b>
                      <strong>Enter Card Details</strong>
                      <span>Fill up correct details of your debit/credit card</span>
                      <small><ChevronDown size={18} /></small>
                    </button>
                    {emiOpen.card && (
                      <div className="confirm-emi-card-form">
                        <label>Enter Your Card No.<input value={emiCard.number} onChange={(event) => setEmiCard((current) => ({ ...current, number: event.target.value }))} placeholder="xxxx-xxxx-xxxx-xxxx" /></label>
                        <label>Enter Card Holder Name.<input value={emiCard.holderName} onChange={(event) => setEmiCard((current) => ({ ...current, holderName: event.target.value }))} placeholder="Card Holder Name" /></label>
                        <div className="confirm-emi-card-row">
                          <label>Expiry Date<select value={emiCard.month} onChange={(event) => setEmiCard((current) => ({ ...current, month: event.target.value }))}><option value="">Month</option><option>01</option><option>02</option><option>03</option><option>04</option><option>05</option><option>06</option><option>07</option><option>08</option><option>09</option><option>10</option><option>11</option><option>12</option></select></label>
                          <label><span>&nbsp;</span><select value={emiCard.year} onChange={(event) => setEmiCard((current) => ({ ...current, year: event.target.value }))}><option value="">Year</option><option>2026</option><option>2027</option><option>2028</option><option>2029</option><option>2030</option></select></label>
                          <label>CVV<input value={emiCard.cvv} onChange={(event) => setEmiCard((current) => ({ ...current, cvv: event.target.value }))} placeholder="CVV" /></label>
                        </div>
                        <div className="confirm-card-pay-row">
                          <div className="confirm-total-fare">
                            <span>Total Fare:</span>
                            <strong>{money(payableTotal)}</strong>
                          </div>
                          <button
  type="button"
  disabled={!isEmiCardReady || paymentLoading}
  onClick={initiateHolidayPayment}
>
  <LockKeyhole size={16} />
  {paymentLoading ? "Processing..." : "Make Payment"}
</button>
                        </div>
                        <p className="confirm-emi-plan-note">
                          {emiBank} {emiType}: {selectedEmiPlan.tenure} months at {formatEmiMoney(selectedEmiPlan.monthly)} per month
                        </p>
                      </div>
                    )}
                  </section>
                  <p className="confirm-secure-note">🔒 We use 128-bit secure encryption providing you a SAFE payment environment</p>
                  <p>
                    By Continuing, you agree to the Rules, <b>Privacy Policy</b>, <b>User Agreement</b> and <b>Terms & Conditions</b> of EaseMyTrip
                  </p>
                </div>
              ) : paymentMode === "Pay with Rewards" ? (
                <div className="confirm-rewards-panel">
                  <h3>Reward Balance : 40 Points</h3>
                  <p className="confirm-rewards-sub">Get up to 1000 TWID Stars Cashback on every spend. Hurry!</p>
                  <div className="confirm-reward-chips">
                    {rewardOptions.map(({ icon, name }) => (
                      <button
                        type="button"
                        key={name}
                        className={selectedReward === name ? "is-active" : ""}
                        onClick={() => setSelectedReward(name)}
                      >
                        <span>{icon}</span>
                        {name}
                      </button>
                    ))}
                  </div>
                  <div className="confirm-card-pay-row">
                    <div className="confirm-total-fare">
                      <span>Total Fare:</span>
                      <strong>{money(payableTotal)}</strong>
                    </div>
                    <button
  type="button"
  disabled={paymentLoading}
  onClick={initiateHolidayPayment}
>
  <LockKeyhole size={16} />
  {paymentLoading ? "Processing..." : "Pay Now"}
</button>
                  </div>
                  <p>
                    By Continuing, you agree to the Rules, <b>Privacy Policy</b>, <b>User Agreement</b> and <b>Terms & Conditions</b> of EaseMyTrip
                  </p>
                </div>
              ) : paymentMode === "Gift Card" ? (
                <div className="confirm-gift-panel">
  <h3>Gift Card</h3>

  <div className="confirm-coupon-input">
    <input
      value={giftCardCode}
      onChange={(event) => setGiftCardCode(event.target.value)}
      placeholder="Enter Gift Card Code"
    />

    <button
      type="button"
      onClick={async () => {
  const code = giftCardCode.trim();

  if (!code) {
    setPackageError("Please enter a gift card code.");
    return;
  }

  if (!pkg?.id || payableBeforeDiscounts <= 0) {
    setPackageError(
      "Unable to validate gift card for this booking."
    );
    return;
  }

  try {
    setPackageError("");

    const response = await fetch(
      `${API_BASE}/api/gift-cards/validate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code,
          package_id: pkg.id,
          booking_amount: Math.max(
            payableBeforeDiscounts - couponDiscount,
            0
          ),
        }),
      }
    );

    const data = await response.json();

    if (!response.ok || !data?.success) {
      setGiftCardApplied(false);
      setGiftCardDiscount(0);
      setPackageError(
        data?.error || "Unable to apply this gift card."
      );
      return;
    }

    const redemption = Math.max(
      0,
      Number(data.redemption_amount || 0)
    );

    setGiftCardDiscount(redemption);
    setGiftCardApplied(true);
  } catch (error) {
    console.error("Gift card validation failed:", error);

    setGiftCardApplied(false);
    setGiftCardDiscount(0);

    setPackageError(
      "Unable to validate the gift card. Please try again."
    );
  }
}}
    >
      Apply
    </button>
  </div>

  {giftCardApplied && (
  <div className="confirm-no-gift">
    <strong>Gift card applied</strong>
    <p>
      {money(giftCardDiscount)} has been
      applied to this booking.
    </p>
  </div>
)}

  <div className="confirm-card-pay-row">
    <div className="confirm-total-fare">
      <span>Total Fare:</span>
      <strong>{money(payableTotal)}</strong>
    </div>

    <button
  type="button"
  disabled={!giftCardApplied || paymentLoading}
  onClick={initiateHolidayPayment}
>
  <LockKeyhole size={16} />
  {paymentLoading ? "Processing..." : "Make Payment"}
</button>
  </div>

                  <p>
                    By Continuing, you agree to the Rules, <b>Privacy Policy</b>, <b>User Agreement</b> and <b>Terms & Conditions</b> of EaseMyTrip
                  </p>
                </div>
              ) : paymentMode === "Google Pay" ? (
                <div className="confirm-google-panel">
                  <div className="confirm-google-logo"><b>G</b><span>Pay</span></div>
                  <p className="confirm-google-sub">Money made simple, by Google</p>
                  <label>Enter Your Google Pay ID</label>
                  <div className="confirm-google-row">
                    <input
                      value={googlePay.id}
                      onChange={(event) => setGooglePay((current) => ({ ...current, id: event.target.value }))}
                      placeholder="Enter Your UPI ID"
                    />
                    <b>@</b>
                    <select value={googlePay.provider} onChange={(event) => setGooglePay((current) => ({ ...current, provider: event.target.value }))}>
                      <option value="">Select</option>
                      <option>okaxis</option>
                      <option>okhdfcbank</option>
                      <option>oksbi</option>
                      <option>upi</option>
                    </select>
                  </div>
                  <div className="confirm-card-pay-row">
                    <div className="confirm-total-fare">
                      <span>Total Fare:</span>
                      <strong>{money(payableTotal)}</strong>
                    </div>
                    <button type="button" disabled={paymentLoading} onClick={initiateHolidayPayment}><LockKeyhole size={16} /> {paymentLoading ? "Processing..." : "Make Payment"}</button>
                  </div>
                  <p>
                    By Continuing, you agree to the Rules, <b>Privacy Policy</b>, <b>User Agreement</b> and <b>Terms & Conditions</b> of EaseMyTrip
                  </p>
                </div>
              ) : (
                <div className="confirm-upi-panel">
                  <label>Enter Your UPI ID</label>
                  <div className="confirm-upi-row">
                    <input
                      value={upi.id}
                      onChange={(event) => setUpi((current) => ({ ...current, id: event.target.value }))}
                      placeholder="ex - mobilenumberupi"
                    />
                    <b>@</b>
                    <select value={upi.provider} onChange={(event) => setUpi((current) => ({ ...current, provider: event.target.value }))}>
                      <option value="">select</option>
                      <option>upi</option>
                      <option>paytm</option>
                      <option>okhdfcbank</option>
                    </select>
                  </div>
                  <div className="upi-pay-footer">
                    <div className="confirm-total-fare">
                      <span>Total Fare:</span>
                      <strong>{money(payableTotal)}</strong>
                    </div>
                    <button
  type="button"
  disabled={paymentLoading}
  onClick={initiateHolidayPayment}
>
  <LockKeyhole size={16} />
  {paymentLoading ? "Processing..." : "Verify & Pay"}
</button>
                  </div>
                  <div className="confirm-pay-steps">
                    <b>How it works?</b>
                    <span>Enter registered VPA</span>
                    <span>Check payment request</span>
                    <span>Approve payment</span>
                  </div>
                  <p>
                    By Continuing, you agree to the Rules, <b>Privacy Policy</b>, <b>User Agreement</b> and <b>Terms & Conditions</b> of EaseMyTrip
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
